import { HttpClient, HttpEvent, HttpEventType, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { AuthState, getAuthState } from 'app/main/auth/store/auth.reducer';
import { getAuthData } from 'app/main/auth/store/auth.selectors';
import { NonConformite } from 'app/main/non-conformite/non-conformite.model';
import { MessageService } from 'primeng/api';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { AppConfigService } from './app-config.service';

export class Media {
    id: number;
    path: string;
    name: string;
}

@Injectable({
    providedIn: 'root'
})
export class MediaService {

    maxFileSizeBytes: number;
    maxFileSize: string;

    constructor(
        private _httpClient: HttpClient,
        private _fuseProgressBarService: FuseProgressBarService,
        private _messageService: MessageService,
        private _translateService: TranslateService,
        private _matDialog: MatDialog,
        private _store: Store<AuthState>,
        private _configService: AppConfigService
    ) {
        
this._store.select(getAuthState)
.pipe(
  filter(data => data.loggedIn == true),
  take(1), 
  switchMap(() =>
    this._httpClient.get(`${this._configService.config.apiUrl}/media/maxFileSize`, {
      responseType: 'text'
    })
  ),
  map((size: string) => {
    // Process the file size into bytes
    const maxFileSizeBytes = Number(size.substring(0, size.toUpperCase().indexOf('MB'))) * 1048576;
    return { size, maxFileSizeBytes };
  })
)
.subscribe(({ size, maxFileSizeBytes }) => {
    this.maxFileSize = size;
    this.maxFileSizeBytes = maxFileSizeBytes;
  },
   err => {
    console.error('Error fetching max file size:', err);
  }
);

      
       
        
    }

    uploadFile(file: File, isImage?: boolean): Observable<Media> {
        if (this.maxFileSizeBytes > file.size) {
            let params: HttpParams = new HttpParams().set('typeFile', isImage ? 'image' : '');
            let formData = new FormData();
            formData.append("files", file, file.name);
            this._fuseProgressBarService.show();
            return this._httpClient.post<Media[]>(`${this._configService.config.apiUrl}/media/upload`, formData, { params: params }).pipe(
                map(
                    response => {
                        this._fuseProgressBarService.hide();
                        return response[0];
                    }
                ),
                catchError(
                    error => {
                        this._messageService.add({ severity: 'error', summary: 'An error occured' });
                        this._fuseProgressBarService.hide();
                        return EMPTY;
                    }
                )
            )
        } else {
            this._translateService.get('App.INVALID-SIZE-MSG').subscribe(msg =>
                this._messageService.add({ severity: 'error', summary: file.name, detail: msg + " " + this.maxFileSize })
            );
            return EMPTY;
        }
    }

    openFile(media: Media, isFullPath?: boolean) {

        let url: string = isFullPath ? media.path : '/media/download/' + media.path;

        this._fuseProgressBarService.show();

        this.downloadFileFromServer(url).subscribe(result => {
            if (result.type === HttpEventType.DownloadProgress) {
                const percentDone = Math.round(100 * result.loaded / result.total);
            } else if (result.type === HttpEventType.Response) {
                this._fuseProgressBarService.hide();
                if (result.body.type.includes('image/')
                    || result.body.type.includes('text/')
                    || result.body.type.includes('application/pdf')
                    || result.body.type.includes('application/json')
                ) {
                    var newBlob = new Blob([result.body], { type: result.body.type });
                    const data = window.URL.createObjectURL(newBlob);
                    window.open(data);

                    // this.dialogRef = this._matDialog.open(MediaComponent, {
                    //     data: { blob: result.body, media: media },
                    //     height: '90%',
                    //     width: '90%',
                    //     panelClass: 'app-media'
                    // });
                } else {
                    this.downloadFile(result.body, media.name, result.body.type);
                }
            }
        }, () => {
            this._fuseProgressBarService.hide();
            this._messageService.add({ severity: 'error', summary: 'An error occured' });
        });
    }

    createMediaAudit(media: Media, isFullPath?: boolean): Observable<Media> {
        let url: string = isFullPath ? media.path : '/media/download/' + media.path;
    
        this._fuseProgressBarService.show();
        media.name = media.name.concat(".pdf");
        return this.downloadFileFromServer(url).pipe(
            filter(event => event.type === HttpEventType.Response),
            switchMap((result: any) => {
                this._fuseProgressBarService.hide();
    
                if (result.body.type.includes('image/') ||
                    result.body.type.includes('text/') ||
                    result.body.type.includes('application/pdf') ||
                    result.body.type.includes('application/json')) {
    
                    let newBlob = new Blob([result.body], { type: result.body.type });
                    let file = new File([newBlob], media.name, { type: result.body.type });
    
                    return this.uploadFile(file); // Upload the blob as a file
                } else {
                    this.downloadFile(result.body, media.name, result.body.type);
                    return of(media); // Return original media object for non-displayable files
                }
            }),
            catchError(() => {
                this._fuseProgressBarService.hide();
                this._messageService.add({ severity: 'error', summary: 'An error occurred' });
                return EMPTY;
            })
        );
    }
   
    private downloadFileFromServer(relativeUrl: string): Observable<HttpEvent<Blob>> {
        return this._httpClient.get(this._configService.config.apiUrl + relativeUrl, { responseType: 'blob', reportProgress: true, observe: 'events' });
    }

    public downloadFile(fileContent: any, fileName: string, mimeType: string) {
        let blob = new Blob([fileContent], { type: mimeType });
        var link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
        link.remove();
    }

    public getBlob(media: Media): Observable<Blob> {
        if (media) {
            return this._httpClient.get(this._configService.config.apiUrl + '/media/download/' + media.path, { responseType: 'blob', observe: 'response' })
                .pipe(map(response => response.body));
        }
        return of(null);
    }
   

    public uploadFileXls(formData: FormData): Observable<NonConformite[]> {
        return this._httpClient.post<any>(
            `${this._configService.config.apiUrl}/media/uploadXls`,
            formData
        );
    }



}